import styled, { css } from 'styled-components';

import { Text } from '@/nova/components';
import { CFG } from '../../../../../theme/configurator-tokens';
import { MOBILE, MOBILE_ANIM } from '../../mobile.tokens';
import { cardHover, hiddenScrollbar } from '../../../../../theme/configurator-keyframes';

const SHEET_H = MOBILE_ANIM.sheetHeight;

export const Screen = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${MOBILE.screenBg};
`;

export const PhoneClip = styled.div`
  position: absolute;
  inset: 35px 0 0 0;
  overflow: hidden;
  border-radius: 16px;
`;

export const PhoneHolder = styled.div<{ $in: boolean }>`
  position: absolute;
  left: 50%;
  top: 50px;
  width: 89.6%;
  transform: translateX(-50%);
  transition: opacity 700ms ease-out;
  opacity: ${({ $in }) => ($in ? 1 : 0)};
`;

export const Scrim = styled.div<{ $shown: boolean }>`
  pointer-events: none;
  position: absolute;
  inset-inline: 0;
  z-index: 10;
  bottom: ${SHEET_H - 50}px;
  height: 150px;
  /* Ride up WITH the sheet: identical translateY + duration + easing as Sheet, so the
     scrim stays glued to the sheet's top edge instead of floating at its final spot
     while the sheet is still travelling (which left a visible gap). Opacity fades in
     over the same window and hides the off-screen sliver before the sheet phase. */
  transition:
    transform ${MOBILE_ANIM.slideMs}ms ease-out,
    opacity ${MOBILE_ANIM.slideMs}ms ease-out ${MOBILE_ANIM.scrimDelayMs}ms;
  transform: ${({ $shown }) => ($shown ? 'translateY(0)' : `translateY(${SHEET_H + 65}px)`)};
  opacity: ${({ $shown }) => ($shown ? 1 : 0)};
  background-image: ${MOBILE.designScrim};
`;

export const PreviewPill = styled.button<{ $shown: boolean }>`
  position: absolute;
  right: 20px;
  z-index: 20;
  bottom: ${SHEET_H + 1}px;
  display: flex;
  height: 48px;
  cursor: pointer;
  align-items: center;
  gap: 8px;
  border-radius: 9999px;
  padding: 0 16px;
  background: none;
  border: none;
  transition: opacity ${MOBILE_ANIM.previewFadeMs}ms ease;
  opacity: ${({ $shown }) => ($shown ? 1 : 0)};
  pointer-events: ${({ $shown }) => ($shown ? 'auto' : 'none')};
`;

export const PreviewPillLabel = styled(Text)`
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: ${MOBILE.white};
`;

export const Sheet = styled.div<{ $up: boolean }>`
  position: absolute;
  inset-inline: 0;
  bottom: 0;
  z-index: 20;
  height: ${SHEET_H}px;
  border-radius: 16px 16px 0 0;
  background: ${MOBILE.sheetBg};
  transition: transform ${MOBILE_ANIM.slideMs}ms ease-out;
  transform: ${({ $up }) => ($up ? 'translateY(0)' : `translateY(${SHEET_H + 65}px)`)};
`;

export const Tabs = styled.div`
  display: flex;
  height: 48px;
  align-items: center;
  gap: 17px;
  padding-left: 24px;
`;

export const Tab = styled.button<{ $active: boolean }>`
  display: flex;
  height: 48px;
  cursor: pointer;
  align-items: center;
  font-size: 14px;
  line-height: 1;
  letter-spacing: -0.6px;
  background: none;
  border: none;
  padding: 0;
  ${({ $active }) =>
    $active
      ? css`
          color: ${CFG.base50};
          font-weight: 700;
        `
      : css`
          color: ${MOBILE.tabIdle};
          font-weight: 500;
        `}
`;

export const Carousel = styled.div`
  ${hiddenScrollbar};
  margin-top: 2px;
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 4px 60px 4px 25px;
  mask-image: ${MOBILE.carouselFade};
  -webkit-mask-image: ${MOBILE.carouselFade};
`;

export const Cta = styled.button<{ $shown: boolean }>`
  position: absolute;
  inset-inline: 10px;
  bottom: 30px;
  z-index: 20;
  display: flex;
  height: 48px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  border: none;
  background: ${CFG.brand};
  padding: 0 16px;
  color: ${MOBILE.knockout};
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${cardHover}
  /* Re-assert absolute placement: the glow mixin declares position: relative, which
     would otherwise unpin this floating CTA. */
  position: absolute;
  transition: opacity ${MOBILE_ANIM.previewFadeMs}ms ease;
  opacity: ${({ $shown }) => ($shown ? 1 : 0)};
  pointer-events: ${({ $shown }) => ($shown ? 'auto' : 'none')};
`;

/* ---- shared card shell ---- */

export const CardButton = styled.button<{ $selected: boolean; $width: number }>`
  position: relative;
  height: 70px;
  flex-shrink: 0;
  cursor: pointer;
  width: ${({ $width }) => $width}px;
  padding: 0;
  border: none;
  background: none;
  box-shadow: ${({ $selected }) => ($selected ? MOBILE.selectGlow : 'none')};
`;

export const SelectRing = styled.span`
  pointer-events: none;
  position: absolute;
  inset: -2px;
  border-radius: 18px;
  border: 2px solid ${MOBILE.selectRing};
`;

export const ThemeFace = styled.span`
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: 16px;
  background: ${MOBILE.designCardBg};
`;

/** Pins the "New" badge inside the tile's top-right corner, clear of the centred logo
 *  (the carousel's scroll box would clip anything hanging outside the 70px tile). */
export const TileBadge = styled.span`
  pointer-events: none;
  position: absolute;
  top: 5px;
  right: 5px;
  display: flex;
`;

export const ColorFace = styled.span<{ $bg: string }>`
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: 16px;
  background: ${({ $bg }) => $bg};
`;

export const ColorAccent = styled.span<{
  $bg: string;
  $left: number;
  $top: number;
  $w: number;
  $h: number;
  $radius: number;
}>`
  position: absolute;
  left: ${({ $left }) => $left}px;
  top: ${({ $top }) => $top}px;
  width: ${({ $w }) => $w}px;
  height: ${({ $h }) => $h}px;
  border-radius: ${({ $radius }) => $radius}px;
  background: ${({ $bg }) => $bg};
`;

export const StyleFace = styled.span`
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: 16px;
  background: ${MOBILE.styleCardBg};
`;

export const FaceImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
